import Image from 'next/legacy/image';
import cn from 'classnames';
import { SubmitHandler, useForm } from 'react-hook-form';

// Components, Images, Style
import { Grid, htToast, Icon, Form, Button, BUTTON_THEMES, InputField } from 'ht-styleguide';
import SlideModal from '@components/Modals/Slide';

/* Segment */
import { SegmentTrackingAction } from '@integrations/segment/geo';

/* Styles */
import styles from './zipcodeentry.module.scss';
import { YupSchemaResolver } from '@constants/validations';

/* Inline Types */
type CustomHeaderProps = { handleClose: BaseAnyFunction };
type CoverageNotAvailableModalProps = { visible: boolean; handleClose: BaseAnyFunction };
type YupFormProps = { email: string };

// See <SlideModal> component for the list of props passed to this header
const CustomHeader = ({ handleClose = () => {} }: CustomHeaderProps) => {
  const iconContainerStyles = cn('flex justifyContentSpaceBetween marginTop-huge1', styles.iconContainer);
  return (
    <header>
      <Grid.Fluid>
        <Grid.Row>
          <Grid.Column sm={4} mdOffset={1} md={6} lgOffset={1} lg={10}>
            <div className={iconContainerStyles}>
              <Icon name="logo-big" className={styles.tealText} />
              <Icon name="close" className="n700" onClick={handleClose} />
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid.Fluid>
    </header>
  );
};

const ZipCodeEntryCoverageModal = ({ visible, handleClose = () => {} }: CoverageNotAvailableModalProps) => {
  /* Text */
  const headerText = "Sorry, we're not in your area yet.";
  const bodyText =
    "We're working to expand our network of technicians to cover more markets including yours. If you’d like, we can give you a heads up when we begin serving your area.";
  const disclaimerText =
    'By selecting Notify Me you agree to receive marketing emails in addition to updates from HelloTech regarding expanded service in your area.';
  const headerTextStyles = cn(styles.tealText, styles.headerText, 'marginBottom-medium1');
  const bodyTextStyles = cn(styles.bodyText, 'h6 marginBottom-large2');

  /* React Hook Form - Hooks */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<YupFormProps>({
    ...YupSchemaResolver(['email'], undefined, {
      email: '',
    }),
  });

  /* Submit the Form */
  const onSubmitForm: SubmitHandler<YupFormProps> = async ({ email }) => {
    SegmentTrackingAction.emailLowTechCoverageSegment({ email });
    htToast('Thank you for your interest!');
    handleClose();
  };

  return (
    <SlideModal open={visible} handleClose={handleClose} customHeader={CustomHeader}>
      <Grid.Fluid>
        <Grid.Row classes="marginTop-large2">
          <Grid.Column sm={4} mdOffset={1} md={6} lgOffset={1} lg={5}>
            <h1 className={headerTextStyles}>{headerText}</h1>
            <p className={bodyTextStyles}>{bodyText}</p>
            <Form classes="marginBottom-medium1" onSubmit={handleSubmit(onSubmitForm)}>
              <Form.Row>
                <Form.Column sm={4} md={8} lg={12}>
                  <InputField type="text" label="Email Address" error={errors.email?.message} {...register('email')} />
                </Form.Column>
              </Form.Row>
              <Form.Row>
                <Form.Column sm={4} md={8} lg={9} classes={styles.customColumn}>
                  <div className={styles.buttonRow}>
                    <Button type="submit" theme={BUTTON_THEMES.PRIMARY} className={styles.confirmButton}>
                      Notify Me
                    </Button>
                    <Button type="button" theme={BUTTON_THEMES.SECONDARY} onClick={handleClose}>
                      No Thanks
                    </Button>
                  </div>
                </Form.Column>
              </Form.Row>
            </Form>
            <p className="p2 n700 marginTop-medium1">{disclaimerText}</p>
          </Grid.Column>
          <Grid.Column classes="showDesktop" lg={6}>
            <div className="imageContainer">
              <Image src="/_nextassets_/images/coverage_map_desktop.png" quality={70} layout="fill" objectFit="scale-down" alt="Map of Coverage" />
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid.Fluid>
      <style jsx>
        {`
          .imageContainer {
            position: relative;
            width: 100%;
          }
        `}
      </style>
    </SlideModal>
  );
};

CustomHeader.displayName = 'CustomHeader.ZipCodeEntryCoverageModal';
export default ZipCodeEntryCoverageModal;
